import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  Code2, HelpCircle, Palette, PlayCircle, ArrowRight, Sparkles, Award, Store,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { SearchInput, PageHeader } from "../ui";
import { usePlan } from "../../contexts/PlanContext";
import { priceLabel, isPaid } from "./marketplaceShared";
import "./Marketplace.scss";

/**
 * MarketplaceHome — the centralized content hub landing page.
 * Sections: category pills, Popular Collections (quiz banks + coding live;
 * themes/videos/AI prompts/certificates marked Soon), Latest Additions with a
 * right filter rail (type + sort).
 */
const PILLS = [
  { key: "all", label: "All" },
  { key: "coding", label: "Coding Questions" },
  { key: "quiz", label: "Quiz Banks" },
  { key: "themes", label: "Themes", soon: true },
  { key: "videos", label: "Videos", soon: true },
  { key: "ai-prompts", label: "AI Prompts", soon: true },
  { key: "certificates", label: "Certificates", soon: true },
];

const MarketplaceHome = () => {
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  const [loading, setLoading] = useState(true);
  const [packs, setPacks] = useState([]);          // published quiz packs (tree tops)
  const [codingMeta, setCodingMeta] = useState({ total: 0, latest: [] });
  const [pill, setPill] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | free | paid
  const [sort, setSort] = useState("newest");

  const quizEnabled = hasPlanFeature("quizAssessment");
  const codingEnabled = hasPlanFeature("codingPractice");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const jobs = [];
      if (quizEnabled) {
        jobs.push(
          apiClient.get("/quiz-categories/marketplace-tree")
            .then(({ data }) => alive && setPacks(data.tree || []))
            .catch(() => {})
        );
      }
      if (codingEnabled) {
        jobs.push(
          apiClient.get("/questions/list", { params: { scope: "library", limit: 5, sort: "newest" } })
            .then(({ data }) => alive && setCodingMeta({ total: data.total || 0, latest: data.questions || [] }))
            .catch(() => {})
        );
      }
      await Promise.all(jobs);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [quizEnabled, codingEnabled]);

  // Merge quiz packs + coding drops into one "latest additions" list.
  const items = useMemo(() => {
    const quizItems = packs.map((p) => ({
      kind: "quiz",
      id: p._id,
      title: p.name,
      icon: p.icon,
      subtitle: `${p.totalCount} questions · ${p.subfolders?.length || 0} topics`,
      pricing: p.pricing,
      installed: p.installed,
      date: p.updatedAt ? new Date(p.updatedAt).getTime() : 0,
      onOpen: () => navigate(`/marketplace/quiz-banks/${p._id}`),
    }));
    const codingItems = codingMeta.latest.map((q) => ({
      kind: "coding",
      id: q._id,
      title: q.title,
      subtitle: [q.difficulty, ...(q.labels || []).slice(0, 3)].filter(Boolean).join(" · "),
      pricing: { model: "free" },
      date: q.createdAt ? new Date(q.createdAt).getTime() : 0,
      onOpen: () => navigate("/marketplace/coding"),
    }));
    let all = [...quizItems, ...codingItems];
    if (pill === "quiz") all = all.filter((i) => i.kind === "quiz");
    if (pill === "coding") all = all.filter((i) => i.kind === "coding");
    if (typeFilter === "free") all = all.filter((i) => !isPaid(i.pricing));
    if (typeFilter === "paid") all = all.filter((i) => isPaid(i.pricing));
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      all = all.filter((i) => i.title.toLowerCase().includes(s));
    }
    all.sort((a, b) => (sort === "oldest" ? a.date - b.date : b.date - a.date));
    return all;
  }, [packs, codingMeta.latest, pill, typeFilter, search, sort, navigate]);

  const totalQuizQuestions = packs.reduce((a, p) => a + (p.totalCount || 0), 0);

  const collections = [
    codingEnabled && {
      key: "coding", tone: "green", icon: <Code2 size={22} />,
      name: "Coding Questions",
      desc: "Improve coding skills with real-world problems",
      foot: `${codingMeta.total}+ questions`,
      onOpen: () => navigate("/marketplace/coding"),
    },
    quizEnabled && {
      key: "quiz", tone: "purple", icon: <HelpCircle size={22} />,
      name: "Quiz Banks",
      desc: "MCQs and assessments for every subject",
      foot: `${packs.length} packs · ${totalQuizQuestions} questions`,
      onOpen: () => navigate("/marketplace/quiz-banks"),
    },
    {
      key: "themes", tone: "orange", icon: <Palette size={22} />,
      name: "Themes", soon: true,
      desc: "Customize your LMS with beautiful themes",
      foot: "Coming soon",
    },
    {
      key: "videos", tone: "blue", icon: <PlayCircle size={22} />,
      name: "Videos", soon: true,
      desc: "Video courses and learning resources",
      foot: "Coming soon",
    },
  ].filter(Boolean);

  return (
    <div className="mkp-root">
      {/* Title + subheading render in the TopNav via the PageHeader portal. */}
      <PageHeader
        icon={Store}
        title="Marketplace"
        description="Discover and install premium content for your institute"
      />
      <div className="mkp-toolbar">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search coding questions, quiz banks, themes..."
        />
      </div>

      <nav className="mkp-pills" aria-label="Content categories">
        {PILLS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`mkp-pill ${pill === p.key ? "mkp-pill--active" : ""}`}
            disabled={p.soon}
            onClick={() => setPill(p.key)}
          >
            {p.label}
            {p.soon && <span className="mkp-pill-soon">Soon</span>}
          </button>
        ))}
      </nav>

      <section>
        <div className="mkp-section-head" style={{ marginBottom: "0.7rem" }}>
          <h2 className="mkp-section-title">Popular Collections</h2>
        </div>
        <div className="mkp-collections">
          {collections.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`mkp-collection mkp-collection--${c.tone} ${c.soon ? "mkp-collection--soon" : ""}`}
              onClick={c.soon ? undefined : c.onOpen}
            >
              <span className="mkp-collection-icon">{c.icon}</span>
              <span className="mkp-collection-name">
                {c.name}
                {c.soon && <span className="mkp-pill-soon">Soon</span>}
              </span>
              <span className="mkp-collection-desc">{c.desc}</span>
              <span className="mkp-collection-foot">
                {c.foot}
                {!c.soon && <ArrowRight size={16} />}
              </span>
            </button>
          ))}
        </div>
      </section>

      <div className="mkp-main">
        <section>
          <div className="mkp-section-head" style={{ marginBottom: "0.7rem" }}>
            <h2 className="mkp-section-title">Latest Additions</h2>
            {quizEnabled && (
              <button type="button" className="mkp-viewall" onClick={() => navigate("/marketplace/quiz-banks")}>
                View all
              </button>
            )}
          </div>

          {loading ? (
            <div className="mkp-loading"><Spinner animation="border" size="sm" /></div>
          ) : items.length === 0 ? (
            <div className="mkp-empty">Nothing here yet — try a different filter, or check back soon.</div>
          ) : (
            <div className="mkp-rows">
              {items.map((item) => (
                <div
                  className="mkp-row"
                  key={`${item.kind}-${item.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={item.onOpen}
                  onKeyDown={(e) => { if (e.key === "Enter") item.onOpen(); }}
                >
                  <span className={`mkp-row-icon mkp-row-icon--${item.kind === "coding" ? "green" : "purple"}`}>
                    {item.kind === "coding" ? <Code2 size={20} /> : (item.icon || <HelpCircle size={20} />)}
                  </span>
                  <div className="mkp-row-main">
                    <div className="mkp-row-title">
                      {item.title}
                      <span className={`mkp-chip ${item.kind === "coding" ? "mkp-chip--coding" : "mkp-chip--type"}`}>
                        {item.kind === "coding" ? "Coding" : "Quiz"}
                      </span>
                      {item.installed?.updates > 0 && (
                        <span className="mkp-chip mkp-chip--update">Update available</span>
                      )}
                      {item.installed?.imported > 0 && item.installed?.updates === 0 && (
                        <span className="mkp-chip mkp-chip--installed">Installed</span>
                      )}
                    </div>
                    <div className="mkp-row-sub">{item.subtitle}</div>
                  </div>
                  <div className="mkp-row-side">
                    <span className={`mkp-price ${isPaid(item.pricing) ? "mkp-price--paid" : "mkp-price--free"}`}>
                      {priceLabel(item.pricing)}
                    </span>
                    <ArrowRight size={16} style={{ color: "var(--text-tertiary)" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="mkp-filter-card">
          <h3 className="mkp-filter-title">Filters</h3>
          <div className="mkp-filter-group">
            <span className="mkp-filter-label">Type</span>
            {[
              { v: "all", label: "All Types" },
              { v: "free", label: "Free" },
              { v: "paid", label: "Paid" },
            ].map((o) => (
              <label className="mkp-filter-option" key={o.v}>
                <input
                  type="radio"
                  name="mkp-type"
                  checked={typeFilter === o.v}
                  onChange={() => setTypeFilter(o.v)}
                />
                {o.label}
              </label>
            ))}
          </div>
          <div className="mkp-filter-group">
            <span className="mkp-filter-label">Sort By</span>
            <select className="mkp-filter-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
          <div className="mkp-filter-group">
            <span className="mkp-filter-label">Coming soon</span>
            <span className="mkp-filter-option" style={{ cursor: "default" }}>
              <Palette size={14} /> Themes
            </span>
            <span className="mkp-filter-option" style={{ cursor: "default" }}>
              <PlayCircle size={14} /> Videos
            </span>
            <span className="mkp-filter-option" style={{ cursor: "default" }}>
              <Sparkles size={14} /> AI Prompts
            </span>
            <span className="mkp-filter-option" style={{ cursor: "default" }}>
              <Award size={14} /> Certificates
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default MarketplaceHome;
